import API from 'constants/api';
import Api from 'util/Api';
import { assertOk, MassiveApiError, toMassiveError } from './MassiveErrors';

const UNREACHABLE =
  'El servicio de creación de envíos no está respondiendo. Volvé a intentar en unos minutos; si sigue igual, avisá a soporte.';

export interface SegaRow {
  [field: string]: string | undefined;
}

export interface SegaShipmentResponse {
  tracking: string;
  workflow_id: string;
  status: string;
  package_id?: number;
  errors?: { row: number; errors: { column: string; message: string }[] }[];
}

const text = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  return String(value);
};

const flag = (value: unknown): string | undefined =>
  value === true ? 'si' : undefined;

const FLEX = 5;
const SAME_DAY = 3;

// Un Flex siempre se crea SameDay: el core le pisa el plazo igual, así que lo
// mandamos resuelto para que el paquete no quede distinto de lo que se envió.
const deliveryTerm = (values: any): string | undefined =>
  values.packageType === FLEX ? String(SAME_DAY) : text(values.deliveryTerm);

interface Resolved {
  lat?: number;
  lng?: number;
  postalCode?: string;
  resolutedAddress?: string;
}

export const toSegaRow = (values: any, destination?: Resolved): SegaRow => {
  const origin = values.shippingScheduleOrigin ?? {};
  const dest = values.shippingScheduleDestination ?? {};
  const originAddress = origin.shippingAddress ?? {};
  const destAddress = dest.shippingAddress ?? {};

  return {
    tracking: text(values.externalCode),
    shipment_type: text(values.packageType),
    content: text(values.caption),
    comments: text(values.detail),
    estimated_cost: text(values.estimatedCost),
    package_size: text(values.packageSize),
    delivery_term: deliveryTerm(values),
    package_qty: text(values.packageQuantity),
    additional_code: text(values.additionalCode),

    origin_name: text(origin.name),
    origin_phone: text(origin.phone),
    origin_address: text(originAddress.addressDetail),
    origin_comments: text(origin.comment),
    origin_address_notes: text(originAddress.comment),

    dest_name: text(dest.name),
    dest_email: text(dest.destinationEmail),
    dest_phone: text(dest.phone),
    dest_address: text(destAddress.addressDetail),
    dest_comments: text(dest.comment),
    dest_address_notes: text(destAddress.comment),

    dest_lat: text(destination?.lat),
    dest_lng: text(destination?.lng),
    dest_zip_code: text(destination?.postalCode),
    dest_resolved_address: text(destination?.resolutedAddress),

    cod_enabled: flag(dest.paymentOnDelivery),
    cod_amount: text(dest.paymentAmount),
    delivery_note: flag(dest.deliveryNote),

    weight: text(values.weight),
    height: text(values.height),
    width: text(values.width),
    length: text(values.length),
    weight_unit: text(values.weightUnit),
    length_unit: text(values.lengthUnit),
  };
};

// SEGA valida con las reglas de la masiva y nombra sus columnas del Excel. Acá
// se traducen a las etiquetas del formulario, que es donde el usuario mira.
const FIELD_LABELS: Record<string, string> = {
  'Nro Seguimiento': 'Seguimiento',
  'Tipo Envío': 'Tipo de envío',
  'Tam Paquete': 'Tamaño',
  'Plazo Entrega': 'Plazo de entrega',
  'Contenido paquete': 'Contenido del paquete',
  'Costo Estimado': 'Costo estimado',
  'Codigo Adicional': 'Código de venta de MELI',
  'Destino Nombre': 'Entregar a',
  'Destino Telefono': 'Teléfono de contacto',
  'Mail Destinatario': 'Email',
  'Destino Dirección': 'Dirección de entrega',
  'Monto Destino': 'Monto a cobrar en destino',
  'Cantidad Bultos': 'Cantidad de bultos',
  'Unidad de Peso': 'Unidad de peso',
};

const fieldLabel = (column: string): string =>
  column
    .split(' / ')
    .map((part) => FIELD_LABELS[part] ?? part)
    .join(' / ');

const rejectionMessage = (body?: SegaShipmentResponse): string => {
  const messages = (body?.errors ?? []).flatMap((row) =>
    row.errors.map((field) => `${fieldLabel(field.column)}: ${field.message}`),
  );
  if (!messages.length) return 'El envío no pasó la validación.';
  return messages.join(' · ');
};

export const createSegaShipment = async (
  row: SegaRow,
  marketplaceId?: number,
  storeId?: number,
): Promise<SegaShipmentResponse> => {
  const params = new URLSearchParams();
  if (marketplaceId) params.append('marketplace_id', String(marketplaceId));
  if (storeId) params.append('store_id', String(storeId));
  try {
    const response = await Api.apiAxios.post(
      `${API.segaShipment.create}?${params.toString()}`,
      row,
    );
    if (response.status === 422) {
      throw new MassiveApiError(422, rejectionMessage(response.data));
    }
    if (response.status === 409) {
      throw new MassiveApiError(
        409,
        'Ya existe un envío con ese número de seguimiento.',
      );
    }
    return assertOk(response, UNREACHABLE);
  } catch (error) {
    throw toMassiveError(error, UNREACHABLE);
  }
};

const LABEL_UNREACHABLE = 'El servicio de etiquetas no está respondiendo.';

const labelBlob = async (url: string, unreachable: string): Promise<Blob> =>
  assertOk(await Api.apiAxios.get(url, { responseType: 'blob' }), unreachable);

export const downloadShipmentLabel = async (
  tracking: string,
  format: 'pdf' | 'zpl' = 'pdf',
  marketplaceId?: number,
): Promise<Blob> => {
  const params = new URLSearchParams({ format });
  if (marketplaceId) params.append('marketplace_id', String(marketplaceId));
  try {
    return await labelBlob(
      `${API.segaShipment.label(tracking)}?${params.toString()}`,
      UNREACHABLE,
    );
  } catch (error) {
    throw toMassiveError(error, UNREACHABLE);
  }
};

const NO_ZPL = 'No hay zpl para este envío';

export const downloadLabelWithFallback = async (
  packageId: number,
  externalCode: string,
  format: 'pdf' | 'zpl' = 'pdf',
  marketplaceId?: number,
): Promise<Blob> => {
  if (externalCode) {
    const params = new URLSearchParams({ format });
    if (marketplaceId) params.append('marketplace_id', String(marketplaceId));
    try {
      return await labelBlob(
        `${API.segaShipment.label(externalCode)}?${params.toString()}`,
        LABEL_UNREACHABLE,
      );
    } catch (error) {
      const failure = toMassiveError(error, LABEL_UNREACHABLE);
      if (format === 'zpl') {
        throw failure.status === 404
          ? new MassiveApiError(404, NO_ZPL)
          : failure;
      }
      if (failure.status !== 404) throw failure;
    }
  }
  if (format === 'zpl') {
    throw new MassiveApiError(404, NO_ZPL);
  }
  try {
    return await labelBlob(
      `${API.packages.label}${packageId}`,
      LABEL_UNREACHABLE,
    );
  } catch (error) {
    throw toMassiveError(error, LABEL_UNREACHABLE);
  }
};
